/**
 * MarketingCalendar (MKT-REDESIGN-01 M5) — the month view: campaigns on the
 * day they run (drag one to another day), the automations' traffic by day
 * (confirmations, reminders, renewals…) so nobody blasts a day the reminders
 * already own, and the SEASONAL PLAN as a light background layer per month
 * (editable, saved in org_setting marketing_seasonal_plan). The existing
 * automations are listed in the same vocabulary with a link to their page.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Check, Loader2, Pencil, Zap } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson, useSaveOrgSetting } from "@/lib/orgSettings";
import { CAMPAIGNS_KEY, STATUS_META, type CampaignStatus } from "./campaigns";

interface CalCampaign { id: string; name: string; status: CampaignStatus; channel: string; audience_label: string | null; recipient_count: number; sent_count: number; day: string; when: string }
interface CalAuto { day: string; key: string; channel: string; status: "sent" | "planned" | "other"; n: number }
interface AutoDef { key: string; label: string; enabled: boolean; channel: string; description: string | null; manual: boolean }
interface CalData { campaigns: CalCampaign[]; automation: CalAuto[]; automations: AutoDef[] }

/** Decision 7: the draft seasonal plan — a starting layer, not a schedule. */
export const DEFAULT_SEASONAL: { month: number; title: string; note: string }[] = [
  { month: 1, title: "Air Quality", note: "Humidifiers, air cleaners, filters — the dry, closed-up months." },
  { month: 2, title: "Air Quality", note: "Second push; furnace safety checks for the late-winter cold." },
  { month: 3, title: "A/C Tune-Ups", note: "Book spring cooling checks before the first warm week." },
  { month: 4, title: "A/C Tune-Ups", note: "Plan members: schedule the included cooling visit." },
  { month: 5, title: "The R-454B Story", note: "Older R-410A systems — what the refrigerant change means for replacement." },
  { month: 6, title: "Cooling Season", note: "No-cool prevention, heat-wave reschedule courtesy." },
  { month: 7, title: "Cooling Season", note: "Mid-season cooling check for members." },
  { month: 8, title: "Early Furnace Checks", note: "Beat the fall rush — early booking window." },
  { month: 9, title: "Fall Tune-Ups", note: "Furnace tune-ups; plan renewals ahead of heating season." },
  { month: 10, title: "Furnace Safety", note: "Carbon monoxide, aging furnaces, replacement conversations." },
  { month: 11, title: "Humidifier Pads", note: "Pads and filters before the heat runs nonstop." },
  { month: 12, title: "Holiday Hours & No-Heat", note: "After-hours coverage, gift-a-tune-up." },
];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const AUTO_SHORT: Record<string, string> = {
  appt_confirm: "confirmations", appt_reminder: "reminders", appt_otw: "on-the-way", appt_late: "running late", invoice_reminder: "invoice nudges",
  membership_renewal: "renewals", quote_nudge: "proposal nudges", review_request: "review asks", post_survey: "surveys", post_referral: "referral", post_warranty: "warranty", post_permit_info: "permit info", post_rebate: "rebate", sms_optin_confirm: "text opt-in confirmations",
};

function useMarketingCalendar(from: string, to: string) {
  return useQuery({
    queryKey: [...CAMPAIGNS_KEY, "calendar", from, to],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CalData> => {
      const { data, error } = await supabase.rpc("pegasus_marketing_calendar", { p_from: from, p_to: to });
      if (error) throw error;
      return data as CalData;
    },
  });
}
function useRescheduleCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; when: string }) => {
      const { error } = await supabase.rpc("pegasus_campaign_reschedule", { p_campaign_id: v.id, p_scheduled_for: v.when });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

export function MarketingCalendar() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const canSend = useCan("marketing.send");
  const seasonalQ = useOrgSettingJson<{ month: number; title: string; note: string }[]>("marketing_seasonal_plan", DEFAULT_SEASONAL);
  const seasonal = seasonalQ.data ?? DEFAULT_SEASONAL;
  const saveSetting = useSaveOrgSetting();
  const reschedule = useRescheduleCampaign();

  // The visible grid: Sunday before the 1st → Saturday after the last day.
  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first); start.setDate(first.getDate() - first.getDay());
    const days: Date[] = [];
    for (let i = 0; i < 42; i += 1) { const d = new Date(start); d.setDate(start.getDate() + i); days.push(d); }
    // trim a trailing empty week
    const lastOfMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    return days.filter((_, i) => i < 35 || days[35] <= lastOfMonth);
  }, [cursor]);
  const from = iso(grid[0]);
  const to = iso(grid[grid.length - 1]);
  const { data, isLoading } = useMarketingCalendar(from, to);
  const byDay = useMemo(() => {
    const m = new Map<string, { campaigns: CalCampaign[]; auto: CalAuto[] }>();
    for (const c of data?.campaigns ?? []) { const e = m.get(c.day) ?? { campaigns: [], auto: [] }; e.campaigns.push(c); m.set(c.day, e); }
    for (const a of data?.automation ?? []) { const e = m.get(a.day) ?? { campaigns: [], auto: [] }; e.auto.push(a); m.set(a.day, e); }
    return m;
  }, [data]);
  const theme = seasonal.find((s) => s.month === cursor.getMonth() + 1);
  const [editTheme, setEditTheme] = useState(false);
  const [themeTitle, setThemeTitle] = useState("");
  const [themeNote, setThemeNote] = useState("");
  const [drag, setDrag] = useState<CalCampaign | null>(null);
  const [pending, setPending] = useState<{ c: CalCampaign; day: string } | null>(null);
  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const saveTheme = () => {
    const next = seasonal.some((s) => s.month === cursor.getMonth() + 1)
      ? seasonal.map((s) => (s.month === cursor.getMonth() + 1 ? { ...s, title: themeTitle.trim(), note: themeNote.trim() } : s))
      : [...seasonal, { month: cursor.getMonth() + 1, title: themeTitle.trim(), note: themeNote.trim() }];
    saveSetting.mutate({ key: "marketing_seasonal_plan", value: next }, { onSuccess: () => setEditTheme(false) });
  };
  const confirmMove = () => {
    if (!pending) return;
    const orig = new Date(pending.c.when);
    const when = new Date(`${pending.day}T${String(orig.getHours()).padStart(2, "0")}:${String(orig.getMinutes()).padStart(2, "0")}:00`);
    reschedule.mutate({ id: pending.c.id, when: when.toISOString() }, { onSettled: () => setPending(null) });
  };

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2">
          <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="rounded-md p-1 text-slate-500 hover:bg-slate-100" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm font-bold text-slate-900">{monthLabel}</span>
          <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="rounded-md p-1 text-slate-500 hover:bg-slate-100" aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
          <button type="button" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} className="rounded-full border border-hairline px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50">Today</button>
          {theme && <span className="ml-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-amber-200">{theme.title}</span>}
          {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
          <InfoTip className="ml-auto" align="end" label="How to read the calendar">
            Chips are campaigns, colored by status. The lightning-bolt number counts the automated messages sent and planned that day; hover it for the breakdown.{canSend ? " Drag a scheduled campaign to another day to move it." : ""}
          </InfoTip>
        </div>
        {pending && (
          <div className="flex flex-wrap items-center gap-2 border-b border-brand-100 bg-brand-50/60 px-3 py-2 text-xs text-slate-700">
            Move <strong>{pending.c.name}</strong> to {new Date(`${pending.day}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} (same time of day)?
            <button type="button" disabled={reschedule.isPending} onClick={confirmMove} className="rounded-md bg-brand-700 px-2.5 py-1 font-semibold text-white hover:bg-brand-800 disabled:opacity-50">{reschedule.isPending ? "Moving…" : "Move"}</button>
            <button type="button" onClick={() => setPending(null)} className="font-medium text-slate-500 hover:text-slate-800">Cancel</button>
            {reschedule.isError && <span className="text-rose-600">{(reschedule.error as Error).message}</span>}
          </div>
        )}
        <div className="grid grid-cols-7 border-b border-slate-100 text-center text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d) => {
            const key = iso(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = key === iso(today);
            const e = byDay.get(key);
            const autoSent = (e?.auto ?? []).filter((a) => a.status === "sent").reduce((s, a) => s + a.n, 0);
            const autoPlanned = (e?.auto ?? []).filter((a) => a.status === "planned").reduce((s, a) => s + a.n, 0);
            const autoTitle = (e?.auto ?? []).map((a) => `${a.n} ${AUTO_SHORT[a.key] ?? a.key} (${a.status})`).join("\n");
            const droppable = !!drag && canSend && inMonth;
            return (
              <div key={key}
                onDragOver={(ev) => { if (droppable) ev.preventDefault(); }}
                onDrop={(ev) => { ev.preventDefault(); if (drag && droppable && drag.day !== key) setPending({ c: drag, day: key }); setDrag(null); }}
                className={cn("min-h-[92px] border-b border-r border-slate-100 p-1.5 align-top", !inMonth && "bg-slate-50/60", droppable && "bg-brand-50/40")}>
                <div className="flex items-center justify-between">
                  <span className={cn("text-[11px] font-semibold", isToday ? "rounded-full bg-brand-700 px-1.5 text-white" : inMonth ? "text-slate-700" : "text-slate-300")}>{d.getDate()}</span>
                  {(autoSent > 0 || autoPlanned > 0) && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400" title={autoTitle}>
                      <Zap className="h-3 w-3" /> {autoSent > 0 ? autoSent : ""}{autoSent > 0 && autoPlanned > 0 ? "+" : ""}{autoPlanned > 0 ? `${autoPlanned}` : ""}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-1">
                  {(e?.campaigns ?? []).map((c) => {
                    const meta = STATUS_META[c.status] ?? STATUS_META.draft;
                    const movable = canSend && (c.status === "scheduled" || c.status === "held");
                    return (
                      <div key={c.id} draggable={movable} onDragStart={() => setDrag(c)} onDragEnd={() => setDrag(null)}
                        title={`${c.name}\n${meta.label} · ${c.audience_label ?? ""} · ${c.recipient_count} recipients${c.sent_count ? ` · ${c.sent_count} sent` : ""}`}
                        className={cn("truncate rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold", meta.chip, movable && "cursor-grab active:cursor-grabbing")}>
                        {c.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="space-y-3">
        <Card className="p-3">
          <div className="flex items-center justify-between gap-2">
            <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              This Month's Theme
              <InfoTip className="normal-case" label="About the monthly theme">A suggested topic for this month's marketing, not a schedule.</InfoTip>
            </h4>
            {canSend && !editTheme && (
              <button type="button" onClick={() => { setThemeTitle(theme?.title ?? ""); setThemeNote(theme?.note ?? ""); setEditTheme(true); }} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Edit theme"><Pencil className="h-3.5 w-3.5" /></button>
            )}
          </div>
          {editTheme ? (
            <div className="mt-2 space-y-2">
              <input value={themeTitle} onChange={(e) => setThemeTitle(e.target.value)} placeholder="Theme" className="h-8 w-full rounded-md border border-hairline bg-white px-2 text-sm" />
              <textarea rows={3} value={themeNote} onChange={(e) => setThemeNote(e.target.value)} placeholder="What to talk about" className="w-full rounded-md border border-hairline bg-white px-2 py-1 text-xs" />
              <div className="flex gap-2">
                <button type="button" disabled={saveSetting.isPending} onClick={saveTheme} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Save</button>
                <button type="button" onClick={() => setEditTheme(false)} className="text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="mt-1">
              <div className="text-sm font-semibold text-slate-900">{theme?.title ?? "No theme yet"}</div>
              <p className="mt-0.5 text-xs text-slate-500">{theme?.note ?? "Set what this month's marketing is about."}</p>
            </div>
          )}
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between gap-2">
            <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Automations
              <InfoTip className="normal-case" label="About automations">Messages that send on their own, such as confirmations, reminders and renewals. Their daily counts show on the calendar so a campaign doesn't land on a busy reminder day.</InfoTip>
            </h4>
            <Link to="/automations" className="text-[11px] font-medium text-brand-700 hover:underline">Open</Link>
          </div>
          <ul className="mt-1.5 space-y-1">
            {(data?.automations ?? []).map((a) => (
              <li key={a.key} className="flex items-center gap-2 text-xs" title={a.description ?? undefined}>
                <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", a.enabled ? "bg-emerald-500" : "bg-slate-300")} />
                <span className={cn("min-w-0 flex-1 truncate", a.enabled ? "text-slate-700" : "text-slate-400")}>{a.label}</span>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400">{a.manual ? "manual" : a.channel}</span>
              </li>
            ))}
            {!data && <li className="text-xs text-slate-400">Loading…</li>}
          </ul>
        </Card>
      </div>
    </div>
  );
}
